/**
 * Selection controls: `MuiSwitch`, `MuiCheckbox`, `MuiRadio` — custom icons, track/thumb sizing, disabled opacity.
 * Discoverability: `MuiSwitch`, `MuiCheckbox`, `MuiRadio`, `Switch`, `Checkbox`, `Radio`.
 *
 * @see https://mui.com/material-ui/api/switch/
 * @see https://mui.com/material-ui/api/checkbox/
 * @see https://mui.com/material-ui/api/radio-group/
 *
 */
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import type { Components, Theme } from '@mui/material/styles';
import * as React from 'react';

export function muiSelection(theme: Theme): Pick<Components<Theme>, 'MuiSwitch' | 'MuiCheckbox' | 'MuiRadio'> {
  const { palette } = theme;
  return {
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 40,
          height: 24,
          padding: 0,
          margin: 9,
        },
        switchBase: {
          padding: 3,
          transitionDuration: '300ms',
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: theme.palette.background.paper,
            '& + .MuiSwitch-track': {
              backgroundColor: theme.palette.primary.main,
              opacity: 1,
              border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: theme.palette.action.disabledOpacity,
              cursor: 'not-allowed',
            },
          },
          '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: theme.palette.primary.main,
            border: `6px solid ${palette.background.layer2}`,
          },
          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: theme.palette.action.disabledOpacity,
            cursor: 'not-allowed',
          },
        },
        thumb: {
          boxSizing: 'border-box',
          width: 18,
          height: 18,
          color: theme.palette.common.white,
        },
        track: {
          borderRadius: 12,
          backgroundColor: theme.palette.background.layer2,
          opacity: 1,
          transition: theme.transitions.create(['background-color'], {
            duration: 500,
          }),
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        checkedIcon: (
          <div
            style={{
              color: theme.palette.background.paper,
              borderRadius: 4,
              fontSize: 18,
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.palette.primary.main,
            }}
          >
            <CheckRoundedIcon sx={{ fontSize: '18px' }} />
          </div>
        ),
        icon: (
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: 4,
              border: `1px solid ${theme.palette.border.main}`,
            }}
          />
        ),
        indeterminateIcon: (
          <div
            style={{
              color: theme.palette.background.paper,
              borderRadius: 4,
              fontSize: 18,
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.palette.primary.main,
            }}
          >
            <RemoveRoundedIcon sx={{ fontSize: '24px' }} />
          </div>
        ),
      },
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            opacity: theme.palette.action.disabledOpacity,
          },
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        checkedIcon: (
          <div
            style={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              border: `1px solid ${theme.palette.primary.main}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main,
              }}
            />
          </div>
        ),
        icon: (
          <div
            style={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              border: `1px solid ${theme.palette.border.dark}`,
            }}
          />
        ),
      },
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            opacity: theme.palette.action.disabledOpacity,
          },
        },
      },
    },
  };
}
